import { newPostBannerShownAtom } from 'app/atoms';
import { withErrorBoundary } from 'app/components/error-boundary';
import { Column } from 'app/design/primitives/box';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';

import { useNavigationOptions } from 'app/hooks/navigation';
import { useAddListingPictures } from 'app/network/listing';
import { usePost, useUpdatePost } from 'app/network/post';
import type { NavigationScreen } from 'app/types/navigation';
import type { ImagePickerAsset } from 'expo-image-picker';
import { useAtom } from 'jotai';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator } from 'react-native';
import { PostComposer, PostComposerAction } from './components/post-composer';

const EditPostScreenComponent: NavigationScreen<'edit-post'> = ({ navigation, route }) => {
  const { postId, listingId } = route.params;
  const { t } = useTranslation('posts');

  useNavigationOptions(navigation, {
    headerTitle: () => (
      <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
        {t('editPost.title')}
      </Text>
    ),
  });

  const { data: post, isLoading: isLoadingPost } = usePost(postId);

  const { mutate: addPictures } = useAddListingPictures(listingId);
  const { mutate: updatePost, isPending } = useUpdatePost({
    onSuccess: () => navigation.goBack(),
  });

  const [draftState, setDraftState] = useState<{
    postId: string;
    text: string;
    images: ImagePickerAsset[];
  }>();
  const [newPostBannerShown, setNewPostBannerShown] = useAtom(newPostBannerShownAtom);

  let draft = draftState;
  if (post && draftState?.postId !== postId) {
    draft = {
      postId,
      text: post.text,
      images: post.pictures.map((pic) => ({
        uri: pic.url,
        width: 0,
        height: 0,
        assetId: pic.id,
      })),
    };
    setDraftState(draft);
  }

  const text = draft?.text ?? '';
  const images = draft?.images ?? [];
  const setText = (nextText: string) => {
    setDraftState((currentDraft) => (currentDraft ? { ...currentDraft, text: nextText } : currentDraft));
  };
  const setImages = (nextImages: ImagePickerAsset[]) => {
    setDraftState((currentDraft) => (currentDraft ? { ...currentDraft, images: nextImages } : currentDraft));
  };

  const handleSubmit = () => {
    if (!text.trim() || !post) return;

    const postPictureIds = new Set(post.pictures.map((p) => p.id));
    const newImages = images.filter((img) => !img.assetId || !postPictureIds.has(img.assetId));

    if (newImages.length > 0) {
      addPictures(newImages);
    }

    updatePost({
      id: postId,
      text: text.trim(),
      pictures: newImages,
    });
  };

  const hasText = text.trim().length > 0;

  if (isLoadingPost) {
    return (
      <Page>
        <Column alignItems="center" flex={1} justifyContent="center">
          <ActivityIndicator />
        </Column>
      </Page>
    );
  }

  return (
    <Page
      accessory={
        <PostComposerAction
          disabled={!hasText || isPending}
          label={t('editPost.submitButton')}
          loading={isPending}
          onPress={handleSubmit}
        />
      }
      keyboardShouldPersistTaps="handled"
      stretchToFill
    >
      {post ? (
        <PostComposer
          loan={post.loan}
          onDismissPublicNotice={() => setNewPostBannerShown(true)}
          onPicturesChange={setImages}
          onTextChange={setText}
          pictures={images}
          showPublicNotice={!newPostBannerShown}
          text={text}
        />
      ) : null}
    </Page>
  );
};

export const EditPostScreen: NavigationScreen<'edit-post'> = withErrorBoundary(EditPostScreenComponent);
